/**
 * Diagram rendering module responsible for drawing Mermaid diagrams
 * Extracts rendering functionality from the main diagram manager
 */
import { resolveDependencies, createEventManager } from './dependency-resolver.js';

/**
 * Creates a diagram renderer with standard dependencies
 * @param {Object} options - Configuration options
 * @returns {Object} - Diagram renderer module
 */
export function createDiagramRenderer(options = {}) {
  const { asyncUtils, eventBus } = resolveDependencies(options);
  const events = createEventManager(eventBus);
  
  /**
   * Initialize mermaid library with consistent configuration
   */
  function initMermaid() {
    if (typeof mermaid === 'undefined') {
      console.warn('Mermaid library not available');
      return;
    }
    
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose'
    });
  }
  
  /**
   * Render a mermaid diagram in the provided container
   * @param {string|HTMLElement} container - The container element or ID
   * @param {string} diagramData - The mermaid diagram code
   * @returns {Promise} - Promise that resolves with the rendered div or rejects with error details
   */
  function renderMermaidDiagram(container, diagramData) {
    return new Promise((resolve, reject) => {
      // Start timing for performance measurement
      const startTime = performance.now();
      
      // Handle container (either ID string or element)
      const diagramDiv = typeof container === 'string' 
        ? document.getElementById(container) 
        : container;
      
      // Validate inputs
      if (!diagramDiv) {
        const error = new Error(`Container not found: ${container}`);
        console.error('Mermaid rendering failed - container not found:', error);
        events.emit('diagram:error', { error });
        reject(error);
        return;
      }
      
      if (!diagramData) {
        const error = new Error('No diagram data provided');
        console.error('Mermaid rendering failed - no data:', error);
        events.emit('diagram:error', { error });
        reject(error);
        return;
      }
      
      const diagramId = 'mermaid-diagram-' + Date.now();
      
      // Remove any loading placeholders
      const pulseDivs = diagramDiv.querySelectorAll('.animate-pulse');
      pulseDivs.forEach(div => div.remove());
      
      // Clear previous content
      diagramDiv.innerHTML = '';
      
      // Create a new div for the diagram with a unique ID
      const newDiagramDiv = document.createElement('div');
      newDiagramDiv.id = diagramId;
      newDiagramDiv.className = 'mermaid';
      newDiagramDiv.textContent = diagramData;
      diagramDiv.appendChild(newDiagramDiv);
      
      // Re-render mermaid diagrams using the latest API approach
      try {
        // Notify that rendering is starting
        events.emit('diagram:renderStart', { containerId: diagramDiv.id });
        
        // Check if mermaid is available
        if (typeof mermaid === 'undefined') {
          throw new Error('Mermaid library not available');
        }
        
        mermaid.run({
          nodes: [newDiagramDiv]
        }).then(async () => {
          // Once mermaid has rendered, find the SVG and ensure it has no positioning styles
          const svg = diagramDiv.querySelector('svg');
          if (svg) {
            // Remove any default positioning that mermaid might add
            svg.style.position = '';
            svg.style.left = '';
            svg.style.top = '';
            svg.style.transform = '';
            
            // Make sure it starts hidden until properly positioned
            svg.classList.remove('svg-ready');
            
            // Wait for next frame to ensure styles are applied
            await asyncUtils.nextFrame();
          }
          
          const endTime = performance.now();
          console.log(`Mermaid diagram rendered successfully in ${(endTime - startTime).toFixed(2)}ms`);
          events.emit('diagram:renderComplete', { 
            containerId: diagramDiv.id,
            success: true,
            renderTime: endTime - startTime
          });
          resolve(newDiagramDiv);
        }).catch(err => {
          console.error('Mermaid diagram rendering error:', err, {
            diagramLength: diagramData.length,
            diagramFirstLine: diagramData.split('\n')[0] || '',
            error: err.message
          });
          events.emit('diagram:renderComplete', { 
            containerId: diagramDiv.id,
            success: false,
            error: err 
          });
          reject(err);
        });
      } catch (e) {
        console.error('Error initializing mermaid rendering:', e, {
          diagramLength: diagramData.length,
          diagramFirstLine: diagramData.split('\n')[0] || '',
          error: e.message
        });
        events.emit('diagram:renderComplete', { 
          containerId: diagramDiv.id,
          success: false,
          error: e 
        });
        reject(e);
      }
    });
  }
  
  /**
   * Create an error display when diagram rendering fails
   * @param {Error} error - The error that occurred
   * @returns {HTMLElement} - The error display element
   */
  function createErrorDisplay(error) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'diagram-error';
    
    // Use the shared error display utility if available
    if (window.errorDisplay && typeof window.errorDisplay.createComponentError === 'function') {
      errorDiv.innerHTML = window.errorDisplay.createComponentError({
        title: 'Failed to render diagram',
        message: 'We couldn\'t display your journey diagram. Try refreshing the page.',
        details: error.message
      });
    } else {
      // Fallback simple error display
      errorDiv.innerHTML = `
        <div class="error-display">
          <h3>Failed to render diagram</h3>
          <p>We couldn't display your journey diagram. Try refreshing the page.</p>
          <details>
            <summary>Error details</summary>
            <code>${error.message || 'Unknown error'}</code>
          </details>
        </div>
      `;
    }
    
    return errorDiv;
  }
  
  /**
   * Clean up resources used by the renderer
   */
  function cleanup() {
    events.cleanup();
  }
  
  // Initialize mermaid when module is created
  initMermaid();
  
  // Return the public API
  return {
    initMermaid,
    renderMermaidDiagram,
    createErrorDisplay,
    cleanup
  };
}